/**
 * State-level location pages for "med spa peptide research" geo SEO.
 * Each state renders at /locations/:slug (e.g., /locations/california).
 *
 * Compliance framing: we are an educational storefront. We describe what
 * peptides are studied in med spa research, and we summarize each state's
 * general med spa regulatory context. We do NOT list specific med spas,
 * prescribe, dispense, or make outcome claims.
 *
 * Every regulatory summary is high-level education — not legal advice.
 * State rules change; always verify with the state medical/nursing board.
 */

// Shared strings (keeps each state entry slimmer without losing uniqueness).
const DEFAULT_CATEGORIES_LEAD =
  "The peptide categories most often referenced in med spa research contexts include:";

export const STATE_LOCATIONS = {
  alabama: {
    slug: "alabama", name: "Alabama", abbrev: "AL",
    medicalBoard: "Alabama Board of Medical Examiners",
    supervisoryRule:
      "Alabama requires that medical procedures performed at a med spa — including injectables, laser treatments, and IV therapy — be delegated by a licensed physician, who must supervise non-physician practitioners and remain accountable for patient outcomes.",
    notableMetros: ["Birmingham", "Huntsville", "Montgomery", "Mobile"],
    peptideContext:
      "Alabama med spas studying peptide protocols typically reference the same research categories — anti-aging (GHK-Cu, glutathione, NAD+), recovery (BPC-157, TB-500), and hormone-axis education (Sermorelin, MOTS-c) — with all compounded or prescription products routed through licensed pharmacies and physician oversight.",
    stateSpecific:
      "The Alabama Board of Medical Examiners has increasingly scrutinized delegation and supervision at aesthetic practices; operators should document physician availability, standing orders, and adverse-event protocols.",
  },
  alaska: {
    slug: "alaska", name: "Alaska", abbrev: "AK",
    medicalBoard: "Alaska State Medical Board",
    supervisoryRule:
      "Alaska permits physician delegation to nurses and PAs for aesthetic procedures, but the practice of medicine — including diagnosis and treatment planning — remains with the licensed physician.",
    notableMetros: ["Anchorage", "Fairbanks", "Juneau"],
    peptideContext:
      "Alaskan clinicians typically operate tele-supervised or multi-site models to reach rural patients; peptide-research education should emphasize FDA-approved GLP-1 context separately from research-only compounds.",
    stateSpecific:
      "Alaska's geography encourages telehealth-enabled medical direction; regulators expect verifiable physician availability and chart review even at distance.",
  },
  arizona: {
    slug: "arizona", name: "Arizona", abbrev: "AZ",
    medicalBoard: "Arizona Medical Board",
    supervisoryRule:
      "Arizona med spas operate under physician medical direction, and the Arizona Medical Board has issued specific guidance on standing orders, patient evaluation, and delegation for aesthetic practices — particularly injectables and IV therapy.",
    notableMetros: ["Scottsdale", "Phoenix", "Tucson", "Mesa", "Chandler"],
    peptideContext:
      "Scottsdale and Phoenix are among the densest med spa markets in the U.S. Peptide research education (GHK-Cu, glutathione, NAD+, PT-141) regularly appears in Arizona longevity and aesthetic programming — always alongside clinician supervision and the distinction between research-only compounds and FDA-approved pharmaceuticals.",
    stateSpecific:
      "Arizona's Board has published explicit med-spa enforcement actions, including where NPs or RNs evaluated patients or injected without documented physician protocols. Scottsdale's concentration makes state regulators especially attentive here.",
  },
  arkansas: {
    slug: "arkansas", name: "Arkansas", abbrev: "AR",
    medicalBoard: "Arkansas State Medical Board",
    supervisoryRule:
      "Arkansas requires physician supervision and delegation for medical procedures performed in med spas. APRNs and PAs may deliver treatments within collaborative agreements and scope-of-practice boundaries.",
    notableMetros: ["Little Rock", "Fayetteville", "Fort Smith"],
    peptideContext:
      "Arkansas med spas evaluating peptide-related research education typically focus on anti-aging and recovery categories, with GLP-1 medications handled strictly through licensed prescribers and pharmacy partners.",
    stateSpecific:
      "The Arkansas Medical Board expects documented medical-director involvement in protocol design, even for low-complexity aesthetic services.",
  },
  california: {
    slug: "california", name: "California", abbrev: "CA",
    medicalBoard: "Medical Board of California",
    supervisoryRule:
      "California is one of the most tightly regulated med spa states. Under the Medical Practice Act and the corporate practice of medicine doctrine, non-physicians may not own medical practices, and aesthetic procedures (injectables, certain lasers, IV therapy) must be performed by or under the direct supervision of a California-licensed physician.",
    notableMetros: ["Los Angeles", "San Francisco", "San Diego", "Beverly Hills", "Sacramento", "San Jose"],
    peptideContext:
      "California leads national interest in peptide-related wellness research — Los Angeles and Beverly Hills alone host a large share of U.S. aesthetic and longevity clinics. Research-category education routinely covers GHK-Cu, glutathione, NAD+, Sermorelin, MOTS-c, BPC-157, TB-500, and PT-141, with strict separation between research-only compounds and FDA-approved prescription products.",
    stateSpecific:
      "The California Attorney General and Medical Board have actively enforced against unlicensed practice and improper delegation at aesthetic businesses. The state also imposes stricter rules on compounded GLP-1s than most states, and practitioners should consult the Board of Pharmacy's latest guidance.",
  },
  colorado: {
    slug: "colorado", name: "Colorado", abbrev: "CO",
    medicalBoard: "Colorado Medical Board",
    supervisoryRule:
      "Colorado permits physician-delegated aesthetic services through PAs and RNs operating under protocols. The Colorado Medical Board and Department of Regulatory Agencies (DORA) have issued med-spa specific guidance emphasizing patient evaluation prior to treatment.",
    notableMetros: ["Denver", "Boulder", "Colorado Springs", "Aspen", "Fort Collins"],
    peptideContext:
      "Denver and Boulder host growing longevity-medicine clusters. Peptide-research education here frequently pairs recovery (BPC-157, TB-500) and hormone-axis (Sermorelin, MOTS-c) discussion with altitude-adapted wellness programming — always routed through licensed clinicians for any actual product use.",
    stateSpecific:
      "Colorado permits NP independent practice with scope limitations, but aesthetic medicine is still treated as the practice of medicine for delegation purposes.",
  },
  connecticut: {
    slug: "connecticut", name: "Connecticut", abbrev: "CT",
    medicalBoard: "Connecticut Medical Examining Board",
    supervisoryRule:
      "Connecticut requires physician oversight for aesthetic medical procedures. Public Act guidance and DPH advisories emphasize that the delegating physician retains medical responsibility.",
    notableMetros: ["Greenwich", "Stamford", "Hartford", "New Haven"],
    peptideContext:
      "Connecticut's affluent Fairfield County corridor concentrates premium aesthetic and longevity practices; peptide-research discussion here tends toward anti-aging (GHK-Cu, NAD+) and hormone-axis education.",
    stateSpecific:
      "Connecticut's small size and dense regulator presence make documentation and physician-availability records especially important.",
  },
  delaware: {
    slug: "delaware", name: "Delaware", abbrev: "DE",
    medicalBoard: "Delaware Board of Medical Licensure & Discipline",
    supervisoryRule:
      "Delaware aesthetic practices operate under physician medical direction, with delegation to NPs, PAs, and RNs within their scope of practice.",
    notableMetros: ["Wilmington", "Dover", "Rehoboth Beach"],
    peptideContext:
      "Delaware med spas evaluating peptide research typically cover anti-aging and recovery categories, with all prescription products routed through licensed pharmacies.",
    stateSpecific:
      "Delaware's proximity to major mid-Atlantic population centers draws cross-state patients; out-of-state prescribing rules apply.",
  },
  florida: {
    slug: "florida", name: "Florida", abbrev: "FL",
    medicalBoard: "Florida Board of Medicine",
    supervisoryRule:
      "Florida is a major med spa market with detailed regulatory guidance. The Florida Board of Medicine requires physician medical direction for aesthetic procedures, and recent legislation has tightened supervision requirements for cosmetic surgery and injectable services.",
    notableMetros: ["Miami", "Orlando", "Tampa", "Fort Lauderdale", "Jacksonville", "Palm Beach", "Naples"],
    peptideContext:
      "Miami and South Florida host one of the country's most competitive aesthetic markets. Peptide-research education — anti-aging (GHK-Cu, glutathione), metabolism (5-Amino-1MQ research-only), recovery (BPC-157, TB-500), and sexual wellness (PT-141) — is widespread, with compliance framing increasingly emphasized after recent Board enforcement.",
    stateSpecific:
      "Florida's 'Office Surgery Rule' and recent enforcement sweeps around unlicensed practice make documented physician availability, informed consent, and protocol review especially critical. Compounded GLP-1 products have drawn specific scrutiny.",
  },
  georgia: {
    slug: "georgia", name: "Georgia", abbrev: "GA",
    medicalBoard: "Georgia Composite Medical Board",
    supervisoryRule:
      "Georgia requires physician delegation and supervision for aesthetic medical procedures, including injectables and laser treatments. Delegation must be documented and the physician must be accessible for consultation.",
    notableMetros: ["Atlanta", "Savannah", "Augusta", "Buckhead"],
    peptideContext:
      "Atlanta (especially Buckhead and the northern suburbs) supports a dense med-spa and longevity-medicine market. Research-peptide education covers the full category range — anti-aging, recovery, hormone, sexual wellness — with standard clinician supervision.",
    stateSpecific:
      "Georgia's Medical Board has issued specific guidance on delegation to nurses at med spas and has acted on cases involving improper supervision.",
  },
  hawaii: {
    slug: "hawaii", name: "Hawaii", abbrev: "HI",
    medicalBoard: "Hawaii Medical Board",
    supervisoryRule:
      "Hawaii med spa aesthetic procedures fall under the practice of medicine, requiring a supervising physician. APRNs may prescribe within scope but must still coordinate with medical direction for aesthetic services.",
    notableMetros: ["Honolulu", "Maui (Kahului/Lahaina)", "Kailua-Kona", "Hilo"],
    peptideContext:
      "Hawaii's wellness-tourism market (Waikiki, Maui, Big Island) blends longevity research education with resort-spa programming. PeptidesForMedSpas is a Honolulu-registered LLC and our editorial emphasis on research-only framing aligns with the state's relatively conservative medical-practice posture.",
    stateSpecific:
      "Hawaii's geographic distance from mainland pharmacies and suppliers makes partner selection and documented sourcing chains particularly important.",
  },
  idaho: {
    slug: "idaho", name: "Idaho", abbrev: "ID",
    medicalBoard: "Idaho Board of Medicine",
    supervisoryRule:
      "Idaho allows independent APRN practice but aesthetic medical procedures still require supervising physician delegation under Board of Medicine guidance.",
    notableMetros: ["Boise", "Coeur d'Alene", "Idaho Falls"],
    peptideContext:
      "Idaho's Boise metro is a growing longevity and wellness market. Peptide-research education here tends toward anti-aging and recovery categories with standard compliance framing.",
    stateSpecific:
      "Idaho's Board has moved toward clearer written delegation expectations at aesthetic practices in recent enforcement.",
  },
  illinois: {
    slug: "illinois", name: "Illinois", abbrev: "IL",
    medicalBoard: "Illinois Department of Financial and Professional Regulation — Medical Board",
    supervisoryRule:
      "Illinois requires physician supervision for aesthetic medical services, with explicit IDFPR guidance on delegation to RNs, APRNs, and PAs. The Medical Practice Act defines the scope of what non-physicians may not do independently.",
    notableMetros: ["Chicago", "Naperville", "Oak Brook", "Schaumburg"],
    peptideContext:
      "Chicago's North Shore and downtown markets support a substantial aesthetic and longevity-medicine community. Research-peptide education covers the full category range with standard clinician-supervised framing.",
    stateSpecific:
      "IDFPR actively investigates unlicensed-practice complaints at med spas; chart documentation and physician availability records are regularly subpoenaed.",
  },
  indiana: {
    slug: "indiana", name: "Indiana", abbrev: "IN",
    medicalBoard: "Indiana Medical Licensing Board",
    supervisoryRule:
      "Indiana requires physician delegation for aesthetic medical procedures. APRN collaborative agreements and PA supervisory relationships define non-physician scope.",
    notableMetros: ["Indianapolis", "Carmel", "Fort Wayne"],
    peptideContext:
      "Indianapolis and Carmel anchor Indiana's aesthetic and longevity market. Peptide-research education here covers the standard category range under licensed-clinician supervision.",
    stateSpecific:
      "Indiana's Board has clarified that non-physician owners of med spas must still contract with medical directors who are genuinely involved — not nominal.",
  },
  iowa: {
    slug: "iowa", name: "Iowa", abbrev: "IA",
    medicalBoard: "Iowa Board of Medicine",
    supervisoryRule:
      "Iowa requires physician oversight for aesthetic medical services, with delegation to NPs, PAs, and RNs within scope.",
    notableMetros: ["Des Moines", "Cedar Rapids", "Iowa City"],
    peptideContext:
      "Iowa's Des Moines and Iowa City markets support aesthetic and longevity practices; peptide-research education follows the standard category framework.",
    stateSpecific:
      "Iowa's Board emphasizes documented physician involvement in protocol design, not just credential lending.",
  },
  kansas: {
    slug: "kansas", name: "Kansas", abbrev: "KS",
    medicalBoard: "Kansas Board of Healing Arts",
    supervisoryRule:
      "Kansas requires physician oversight for aesthetic medical services performed at med spas, with delegation to APRNs, PAs, and RNs under the Kansas Healing Arts Act.",
    notableMetros: ["Wichita", "Kansas City (KS)", "Overland Park"],
    peptideContext:
      "The Kansas City metro (KS side) and Wichita anchor the state's aesthetic market. Peptide-research education covers standard categories with licensed-clinician oversight.",
    stateSpecific:
      "Kansas's Healing Arts Board has enforced against medical-director arrangements that lack real physician involvement.",
  },
  kentucky: {
    slug: "kentucky", name: "Kentucky", abbrev: "KY",
    medicalBoard: "Kentucky Board of Medical Licensure",
    supervisoryRule:
      "Kentucky aesthetic practices operate under physician medical direction with delegation to APRNs and PAs within scope.",
    notableMetros: ["Louisville", "Lexington"],
    peptideContext:
      "Louisville and Lexington support the state's primary aesthetic-medicine markets; peptide-research education follows the standard research-only framing.",
    stateSpecific:
      "Kentucky's Board of Medical Licensure has issued guidance tightening expectations for physician availability at aesthetic practices.",
  },
  louisiana: {
    slug: "louisiana", name: "Louisiana", abbrev: "LA",
    medicalBoard: "Louisiana State Board of Medical Examiners",
    supervisoryRule:
      "Louisiana requires physician supervision for aesthetic medical services. APRN collaborative practice agreements and PA supervisory relationships apply.",
    notableMetros: ["New Orleans", "Baton Rouge", "Lafayette"],
    peptideContext:
      "New Orleans and Baton Rouge anchor Louisiana's aesthetic market. Peptide-research education covers standard categories under licensed-clinician supervision.",
    stateSpecific:
      "Louisiana has detailed med-spa specific guidance from the Board of Medical Examiners emphasizing proper delegation and informed consent.",
  },
  maine: {
    slug: "maine", name: "Maine", abbrev: "ME",
    medicalBoard: "Maine Board of Licensure in Medicine",
    supervisoryRule:
      "Maine allows APRN independent practice but requires physician delegation for aesthetic medical services. The Board of Licensure in Medicine oversees physician supervision obligations.",
    notableMetros: ["Portland", "Bangor"],
    peptideContext:
      "Maine's Portland metro supports a compact but growing aesthetic and longevity market; peptide-research education follows standard research-only framing.",
    stateSpecific:
      "Maine's regulatory posture is relatively conservative; documentation of physician involvement is expected even at low-complexity aesthetic practices.",
  },
  maryland: {
    slug: "maryland", name: "Maryland", abbrev: "MD",
    medicalBoard: "Maryland Board of Physicians",
    supervisoryRule:
      "Maryland's Board of Physicians has issued explicit med-spa guidance. Aesthetic medical procedures — including injectables and IV therapy — must be performed under physician medical direction with documented delegation.",
    notableMetros: ["Baltimore", "Bethesda", "Rockville", "Annapolis"],
    peptideContext:
      "The DC-Baltimore corridor (Bethesda, Rockville) hosts premium aesthetic and longevity practices. Peptide-research education covers the standard category range under licensed-clinician supervision.",
    stateSpecific:
      "Maryland has among the most explicit med-spa advisories in the country, and the Board has enforced against clinics where physician involvement was nominal.",
  },
  massachusetts: {
    slug: "massachusetts", name: "Massachusetts", abbrev: "MA",
    medicalBoard: "Massachusetts Board of Registration in Medicine",
    supervisoryRule:
      "Massachusetts requires physician supervision for aesthetic medical procedures. The Board of Registration in Medicine takes a strict view of delegation and the corporate practice of medicine doctrine.",
    notableMetros: ["Boston", "Cambridge", "Newton", "Brookline"],
    peptideContext:
      "Boston's dense academic-medicine environment produces sophisticated peptide-research discourse; longevity-medicine practices here typically emphasize evidence-based framing and clinician supervision.",
    stateSpecific:
      "Massachusetts enforces the corporate practice of medicine doctrine — non-physicians generally cannot own medical practices — which constrains med spa ownership structures.",
  },
  michigan: {
    slug: "michigan", name: "Michigan", abbrev: "MI",
    medicalBoard: "Michigan Board of Medicine",
    supervisoryRule:
      "Michigan requires physician oversight for aesthetic medical services with delegation to APRNs, PAs, and RNs.",
    notableMetros: ["Detroit", "Grand Rapids", "Ann Arbor", "Bloomfield Hills"],
    peptideContext:
      "Detroit's suburbs (Bloomfield Hills, Birmingham) and Grand Rapids support Michigan's aesthetic-medicine market; peptide-research education covers the standard category range.",
    stateSpecific:
      "Michigan's LARA department has issued guidance clarifying that med spa services constitute the practice of medicine even when performed by licensed nurses.",
  },
  minnesota: {
    slug: "minnesota", name: "Minnesota", abbrev: "MN",
    medicalBoard: "Minnesota Board of Medical Practice",
    supervisoryRule:
      "Minnesota allows APRN independent practice under Board of Nursing scope but aesthetic medical services typically still require physician delegation for procedures defined as the practice of medicine.",
    notableMetros: ["Minneapolis", "St. Paul", "Edina", "Rochester"],
    peptideContext:
      "The Twin Cities (Minneapolis, St. Paul, Edina) and Rochester (Mayo Clinic area) support substantial aesthetic and longevity-medicine practices with evidence-forward clinical culture.",
    stateSpecific:
      "Minnesota's proximity to Mayo Clinic creates a higher-than-average expectation of evidence-based framing in aesthetic and longevity discussions.",
  },
  mississippi: {
    slug: "mississippi", name: "Mississippi", abbrev: "MS",
    medicalBoard: "Mississippi State Board of Medical Licensure",
    supervisoryRule:
      "Mississippi requires physician supervision for aesthetic medical procedures, with APRN collaborative agreements governing nurse-practitioner scope.",
    notableMetros: ["Jackson", "Gulfport", "Oxford"],
    peptideContext:
      "Jackson and coastal Mississippi support the state's aesthetic market; peptide-research education follows standard research-only framing.",
    stateSpecific:
      "Mississippi's Board has focused on delegation and physician availability at med spas in recent guidance.",
  },
  missouri: {
    slug: "missouri", name: "Missouri", abbrev: "MO",
    medicalBoard: "Missouri Board of Registration for the Healing Arts",
    supervisoryRule:
      "Missouri requires physician supervision for aesthetic medical procedures performed at med spas.",
    notableMetros: ["Kansas City", "St. Louis", "Springfield"],
    peptideContext:
      "Kansas City and St. Louis support Missouri's aesthetic and longevity market; peptide-research education covers the standard category range under clinician supervision.",
    stateSpecific:
      "Missouri's Healing Arts Board expects documented medical-director involvement at aesthetic practices.",
  },
  montana: {
    slug: "montana", name: "Montana", abbrev: "MT",
    medicalBoard: "Montana Board of Medical Examiners",
    supervisoryRule:
      "Montana allows APRN independent practice but aesthetic medical procedures are still governed by physician-delegation rules under the Medical Practice Act.",
    notableMetros: ["Billings", "Missoula", "Bozeman"],
    peptideContext:
      "Bozeman, Missoula, and Billings support a growing longevity and aesthetic market. Peptide-research education follows standard research-only framing.",
    stateSpecific:
      "Montana's large geography encourages tele-supervised models; regulators still expect real physician involvement.",
  },
  nebraska: {
    slug: "nebraska", name: "Nebraska", abbrev: "NE",
    medicalBoard: "Nebraska Board of Medicine & Surgery",
    supervisoryRule:
      "Nebraska requires physician oversight for aesthetic medical services with APRN and PA delegation under scope.",
    notableMetros: ["Omaha", "Lincoln"],
    peptideContext:
      "Omaha and Lincoln anchor the state's aesthetic market; peptide-research education covers standard categories.",
    stateSpecific:
      "Nebraska's Board emphasizes documented physician involvement in protocol design.",
  },
  nevada: {
    slug: "nevada", name: "Nevada", abbrev: "NV",
    medicalBoard: "Nevada State Board of Medical Examiners",
    supervisoryRule:
      "Nevada requires physician medical direction for aesthetic procedures. The state has explicit guidance on delegation and a track record of enforcement against unlicensed practice.",
    notableMetros: ["Las Vegas", "Reno", "Henderson"],
    peptideContext:
      "Las Vegas is a major U.S. aesthetic and longevity market. Peptide-research education here covers the full category range — anti-aging, metabolism, recovery, hormone, and sexual wellness — with sharp compliance framing due to regulator attention.",
    stateSpecific:
      "Nevada has actively investigated unlicensed practice at aesthetic businesses, especially on the Las Vegas Strip and in Henderson.",
  },
  "new-hampshire": {
    slug: "new-hampshire", name: "New Hampshire", abbrev: "NH",
    medicalBoard: "New Hampshire Board of Medicine",
    supervisoryRule:
      "New Hampshire allows APRN independent practice but aesthetic medical services require physician delegation under the Medical Practice Act.",
    notableMetros: ["Manchester", "Nashua", "Portsmouth"],
    peptideContext:
      "Southern New Hampshire (Manchester, Nashua) supports the state's aesthetic market; peptide-research education follows standard framing.",
    stateSpecific:
      "New Hampshire's Board is relatively proactive about med-spa supervision documentation.",
  },
  "new-jersey": {
    slug: "new-jersey", name: "New Jersey", abbrev: "NJ",
    medicalBoard: "New Jersey State Board of Medical Examiners",
    supervisoryRule:
      "New Jersey has strict med-spa regulation. The Board of Medical Examiners requires that cosmetic medical procedures be performed by or under the direct supervision of a physician, and NJ enforces the corporate practice of medicine doctrine.",
    notableMetros: ["Newark", "Jersey City", "Hoboken", "Princeton", "Morristown"],
    peptideContext:
      "Northern NJ (Morristown, Princeton, Hoboken) supports premium aesthetic and longevity practices. Peptide-research education covers standard categories with notably strict compliance framing.",
    stateSpecific:
      "New Jersey prohibits non-physician ownership of medical practices and has actively enforced against 'management companies' that cross into the practice of medicine.",
  },
  "new-mexico": {
    slug: "new-mexico", name: "New Mexico", abbrev: "NM",
    medicalBoard: "New Mexico Medical Board",
    supervisoryRule:
      "New Mexico allows APRN independent practice, but aesthetic medical procedures require physician delegation where the Medical Practice Act applies.",
    notableMetros: ["Albuquerque", "Santa Fe"],
    peptideContext:
      "Santa Fe's wellness-tourism market and Albuquerque's broader aesthetic practice base cover standard research-category education.",
    stateSpecific:
      "New Mexico's Medical Board has issued clarifications on where nurse-led aesthetic practice still requires physician oversight.",
  },
  "new-york": {
    slug: "new-york", name: "New York", abbrev: "NY",
    medicalBoard: "New York State Office of Professional Medical Conduct (OPMC) / Board for Medicine",
    supervisoryRule:
      "New York strictly enforces the corporate practice of medicine doctrine — non-physicians may not own medical practices — and requires physician supervision for all aesthetic medical procedures. New York's rules on management-services organizations (MSOs) are among the most detailed in the country.",
    notableMetros: ["New York City", "Manhattan", "Brooklyn", "Long Island", "Westchester", "Buffalo"],
    peptideContext:
      "Manhattan, Brooklyn, and Long Island host one of the most competitive aesthetic and longevity markets in the U.S. Peptide-research education covers the full category range — GHK-Cu, glutathione, NAD+, Sermorelin, MOTS-c, BPC-157, TB-500, PT-141 — under notably strict compliance framing.",
    stateSpecific:
      "New York's Attorney General and Department of Health have investigated improperly structured med spas. Any operating model should be reviewed by New York counsel for CPOM and MSO compliance.",
  },
  "north-carolina": {
    slug: "north-carolina", name: "North Carolina", abbrev: "NC",
    medicalBoard: "North Carolina Medical Board",
    supervisoryRule:
      "North Carolina requires physician supervision for aesthetic medical procedures. The Medical Board has issued guidance specifically addressing med spa delegation and documentation.",
    notableMetros: ["Charlotte", "Raleigh", "Durham", "Asheville"],
    peptideContext:
      "The Research Triangle (Raleigh-Durham) and Charlotte support substantial aesthetic and longevity markets. Peptide-research education covers the standard category range with evidence-forward framing common in academic-medical regions.",
    stateSpecific:
      "The NC Medical Board has enforced actions against med spas with inadequate physician involvement.",
  },
  "north-dakota": {
    slug: "north-dakota", name: "North Dakota", abbrev: "ND",
    medicalBoard: "North Dakota Board of Medicine",
    supervisoryRule:
      "North Dakota requires physician oversight for aesthetic medical services, with APRN and PA delegation within scope.",
    notableMetros: ["Fargo", "Bismarck"],
    peptideContext:
      "Fargo anchors North Dakota's aesthetic market; peptide-research education follows standard framing.",
    stateSpecific:
      "North Dakota's Board focuses on documented physician availability at aesthetic practices.",
  },
  ohio: {
    slug: "ohio", name: "Ohio", abbrev: "OH",
    medicalBoard: "State Medical Board of Ohio",
    supervisoryRule:
      "Ohio requires physician supervision for aesthetic medical procedures. The State Medical Board has issued detailed guidance on delegation to APRNs, PAs, and cosmetic therapists.",
    notableMetros: ["Columbus", "Cleveland", "Cincinnati"],
    peptideContext:
      "Columbus, Cleveland (Cleveland Clinic area), and Cincinnati support substantial aesthetic and longevity markets; peptide-research education covers the standard category range.",
    stateSpecific:
      "Ohio's Medical Board has published specific guidance on cosmetic-therapist scope and the distinction between delegation and independent practice.",
  },
  oklahoma: {
    slug: "oklahoma", name: "Oklahoma", abbrev: "OK",
    medicalBoard: "Oklahoma State Board of Medical Licensure & Supervision",
    supervisoryRule:
      "Oklahoma requires physician oversight for aesthetic medical services with APRN collaborative practice and PA supervisory relationships.",
    notableMetros: ["Oklahoma City", "Tulsa"],
    peptideContext:
      "Oklahoma City and Tulsa support the state's aesthetic market; peptide-research education follows standard research-only framing.",
    stateSpecific:
      "Oklahoma's Board emphasizes written delegation protocols and documented physician involvement.",
  },
  oregon: {
    slug: "oregon", name: "Oregon", abbrev: "OR",
    medicalBoard: "Oregon Medical Board",
    supervisoryRule:
      "Oregon allows APRN independent practice but aesthetic medical procedures still fall under physician-delegation rules for the practice of medicine.",
    notableMetros: ["Portland", "Eugene", "Bend"],
    peptideContext:
      "Portland and Bend host growing aesthetic and longevity-medicine markets. Peptide-research education covers the standard category range.",
    stateSpecific:
      "Oregon's Board has clarified that cosmetic procedures performed by nurses still require physician medical direction.",
  },
  pennsylvania: {
    slug: "pennsylvania", name: "Pennsylvania", abbrev: "PA",
    medicalBoard: "Pennsylvania State Board of Medicine",
    supervisoryRule:
      "Pennsylvania requires physician oversight for aesthetic medical procedures. The Board has specific guidance on delegation to NPs, PAs, and RNs at med spas.",
    notableMetros: ["Philadelphia", "Pittsburgh", "Main Line"],
    peptideContext:
      "Philadelphia's Main Line and Pittsburgh support substantial aesthetic and longevity practices. Peptide-research education covers the standard category range.",
    stateSpecific:
      "Pennsylvania's Board has enforced against med spas operating without appropriate physician medical direction.",
  },
  "rhode-island": {
    slug: "rhode-island", name: "Rhode Island", abbrev: "RI",
    medicalBoard: "Rhode Island Board of Medical Licensure & Discipline",
    supervisoryRule:
      "Rhode Island requires physician oversight for aesthetic medical procedures, with APRN and PA delegation under collaborative/supervisory agreements.",
    notableMetros: ["Providence", "Newport"],
    peptideContext:
      "Providence and Newport support the state's compact aesthetic market; peptide-research education follows standard framing.",
    stateSpecific:
      "Rhode Island's small size concentrates regulator attention on med-spa compliance.",
  },
  "south-carolina": {
    slug: "south-carolina", name: "South Carolina", abbrev: "SC",
    medicalBoard: "South Carolina Board of Medical Examiners",
    supervisoryRule:
      "South Carolina requires physician oversight for aesthetic medical services, with APRN and PA delegation under scope.",
    notableMetros: ["Charleston", "Greenville", "Columbia", "Hilton Head"],
    peptideContext:
      "Charleston and Greenville support growing aesthetic and longevity markets; peptide-research education covers standard categories.",
    stateSpecific:
      "South Carolina's Board has clarified delegation expectations at aesthetic practices in recent guidance.",
  },
  "south-dakota": {
    slug: "south-dakota", name: "South Dakota", abbrev: "SD",
    medicalBoard: "South Dakota Board of Medical & Osteopathic Examiners",
    supervisoryRule:
      "South Dakota requires physician oversight for aesthetic medical services, with APRN and PA delegation.",
    notableMetros: ["Sioux Falls", "Rapid City"],
    peptideContext:
      "Sioux Falls anchors the state's aesthetic market; peptide-research education follows standard framing.",
    stateSpecific:
      "South Dakota's Board emphasizes documented physician availability at aesthetic practices.",
  },
  tennessee: {
    slug: "tennessee", name: "Tennessee", abbrev: "TN",
    medicalBoard: "Tennessee Board of Medical Examiners",
    supervisoryRule:
      "Tennessee requires physician supervision for aesthetic medical procedures, with detailed guidance on APRN and PA delegation from the Board of Medical Examiners.",
    notableMetros: ["Nashville", "Memphis", "Knoxville", "Chattanooga"],
    peptideContext:
      "Nashville's booming health and wellness market supports substantial aesthetic and longevity practices. Peptide-research education covers the standard category range.",
    stateSpecific:
      "Tennessee has explicit med-spa guidance and has acted on physician-involvement deficiencies at aesthetic practices.",
  },
  texas: {
    slug: "texas", name: "Texas", abbrev: "TX",
    medicalBoard: "Texas Medical Board",
    supervisoryRule:
      "Texas is a tightly regulated med-spa state. The Texas Medical Board requires physician delegation for aesthetic medical services, enforces the corporate practice of medicine doctrine, and has published specific advisories on med-spa operations and delegation to NPs, PAs, and RNs.",
    notableMetros: ["Dallas", "Houston", "Austin", "San Antonio", "Fort Worth", "Plano"],
    peptideContext:
      "Dallas, Houston, Austin, and the broader Texas metros host one of the largest U.S. aesthetic and longevity markets. Peptide-research education — anti-aging (GHK-Cu, NAD+), metabolism (5-Amino-1MQ — research-only), recovery (BPC-157, TB-500), hormone (Sermorelin, MOTS-c), and sexual wellness (PT-141) — is widespread, with compliance framing increasingly central after TMB enforcement.",
    stateSpecific:
      "The Texas Medical Board has been especially active on CPOM enforcement, management-services organization structures, and physician-involvement at non-physician-owned med spas. Compounded GLP-1 products also draw regulator attention.",
  },
  utah: {
    slug: "utah", name: "Utah", abbrev: "UT",
    medicalBoard: "Utah Division of Occupational & Professional Licensing (DOPL)",
    supervisoryRule:
      "Utah requires physician oversight for aesthetic medical procedures, with APRN and PA delegation governed by DOPL rules.",
    notableMetros: ["Salt Lake City", "Park City", "Provo"],
    peptideContext:
      "Salt Lake City and Park City support growing aesthetic and longevity markets, especially around the wellness-tourism corridor. Peptide-research education covers the standard category range.",
    stateSpecific:
      "Utah's DOPL has issued guidance on cosmetic-procedure delegation and physician availability.",
  },
  vermont: {
    slug: "vermont", name: "Vermont", abbrev: "VT",
    medicalBoard: "Vermont Board of Medical Practice",
    supervisoryRule:
      "Vermont allows APRN independent practice but aesthetic medical procedures require physician delegation.",
    notableMetros: ["Burlington", "Stowe"],
    peptideContext:
      "Burlington and Stowe support Vermont's compact aesthetic and wellness market; peptide-research education follows standard framing.",
    stateSpecific:
      "Vermont's Board emphasizes thorough documentation of physician involvement even at small aesthetic practices.",
  },
  virginia: {
    slug: "virginia", name: "Virginia", abbrev: "VA",
    medicalBoard: "Virginia Board of Medicine",
    supervisoryRule:
      "Virginia requires physician oversight for aesthetic medical services, with detailed Board of Medicine guidance on med-spa supervision.",
    notableMetros: ["Arlington", "Alexandria", "Richmond", "Virginia Beach", "Tysons"],
    peptideContext:
      "Northern Virginia (Arlington, Tysons, Alexandria) and Richmond support premium aesthetic and longevity-medicine practices. Peptide-research education covers the standard category range.",
    stateSpecific:
      "Virginia's Board has issued med-spa advisories emphasizing physician involvement and proper delegation to non-physician practitioners.",
  },
  washington: {
    slug: "washington", name: "Washington", abbrev: "WA",
    medicalBoard: "Washington Medical Commission",
    supervisoryRule:
      "Washington allows APRN independent practice but aesthetic medical procedures still require physician delegation where they constitute the practice of medicine.",
    notableMetros: ["Seattle", "Bellevue", "Tacoma", "Spokane"],
    peptideContext:
      "Seattle and Bellevue support a substantial aesthetic and longevity-medicine market. Peptide-research education covers the standard category range with evidence-forward framing common in the region.",
    stateSpecific:
      "The Washington Medical Commission has issued guidance on aesthetic-practice delegation; written protocols and physician availability are emphasized.",
  },
  "west-virginia": {
    slug: "west-virginia", name: "West Virginia", abbrev: "WV",
    medicalBoard: "West Virginia Board of Medicine",
    supervisoryRule:
      "West Virginia requires physician oversight for aesthetic medical services, with APRN collaborative agreements and PA supervision.",
    notableMetros: ["Charleston", "Morgantown"],
    peptideContext:
      "West Virginia's aesthetic market is compact; peptide-research education follows standard framing.",
    stateSpecific:
      "The West Virginia Board emphasizes documented physician involvement at aesthetic practices.",
  },
  wisconsin: {
    slug: "wisconsin", name: "Wisconsin", abbrev: "WI",
    medicalBoard: "Wisconsin Medical Examining Board",
    supervisoryRule:
      "Wisconsin requires physician supervision for aesthetic medical procedures, with APRN and PA delegation under scope.",
    notableMetros: ["Milwaukee", "Madison"],
    peptideContext:
      "Milwaukee and Madison support Wisconsin's aesthetic and longevity market; peptide-research education covers standard categories.",
    stateSpecific:
      "Wisconsin's Medical Examining Board has issued guidance emphasizing genuine physician involvement in aesthetic-practice delegation.",
  },
  wyoming: {
    slug: "wyoming", name: "Wyoming", abbrev: "WY",
    medicalBoard: "Wyoming Board of Medicine",
    supervisoryRule:
      "Wyoming requires physician oversight for aesthetic medical services, with APRN and PA delegation under scope.",
    notableMetros: ["Cheyenne", "Jackson", "Casper"],
    peptideContext:
      "Jackson's wellness-tourism market and Cheyenne's broader aesthetic-practice base cover standard peptide-research category education.",
    stateSpecific:
      "Wyoming's large geography encourages tele-supervised medical direction; regulators still expect genuine physician involvement.",
  },
  "district-of-columbia": {
    slug: "district-of-columbia", name: "District of Columbia", abbrev: "DC",
    medicalBoard: "District of Columbia Board of Medicine",
    supervisoryRule:
      "DC requires physician oversight for aesthetic medical procedures, with APRN and PA delegation under DC Health rules.",
    notableMetros: ["Washington, DC", "Georgetown"],
    peptideContext:
      "Washington, DC and Georgetown support premium aesthetic and longevity practices; peptide-research education covers the standard category range.",
    stateSpecific:
      "DC's regulatory posture mirrors Maryland and Virginia in its expectation of documented physician involvement.",
  },
};

export const STATE_SLUGS = Object.keys(STATE_LOCATIONS);

export { DEFAULT_CATEGORIES_LEAD };
